/**
 * applySiteScore.ts — out-of-order guard for the site-score request lifecycle (T-234)
 *
 * Race (per the T-234 trace): rapid successive selections — pin-drop or search — each fire a
 * `/api/score/site` request via `fetchSiteScore`. These requests can resolve OUT OF ORDER: if an
 * earlier request's response lands after a later one, the naive path applies the earlier (stale)
 * result last, so the panel shows a score for a location the user already moved away from. The
 * earlier request's completion also flips `loading` off while the latest request is still in flight.
 *
 * This helper applies the result/error only if the request is STILL the latest when it resolves
 * (an apply-time guard against a caller-owned `isCurrent` predicate — the same shape as the T-233
 * activeTractRef staleness guard), and clears loading (`finish`) only for that latest request. The
 * caller (MapShell.fetchAndSetSiteScore) owns a monotonic request-id ref: it increments the ref and
 * runs the synchronous loading/reset before calling here, then passes `isCurrent = () => reqId ===
 * ref.current`. Scope is the site-score lifecycle ONLY — no tract/panel/hydration behaviour.
 */
import type { SiteScoreResult } from './tractModel'

export interface ApplySiteScoreDeps {
  /** The site-score request (POST /api/score/site) for these coordinates. */
  fetchSiteScore: (lat: number, lng: number) => Promise<SiteScoreResult>
  /** True iff this request is still the latest one when its response resolves (staleness guard). */
  isCurrent: () => boolean
  /** Commit the score result to app state (setSiteScoreResult). */
  applyResult: (result: SiteScoreResult) => void
  /** Commit the error copy to app state (setSiteScoreError). */
  applyError: (message: string) => void
  /** Clear the loading flag (setSiteScoreLoading(false)) — invoked only when still current. */
  finish: () => void
  /** Map a thrown fetch error to user-facing copy (404 → no-coverage, else unavailable). */
  mapError: (err: unknown) => string
}

/**
 * Run a site-score fetch and apply its outcome, guarded against staleness. A superseded request
 * (a newer selection started while this one was in flight) is dropped: it neither overwrites the
 * current result/error nor clears loading. Never throws.
 */
export async function fetchAndApplySiteScore(
  lat: number,
  lng: number,
  deps: ApplySiteScoreDeps,
): Promise<void> {
  try {
    const result = await deps.fetchSiteScore(lat, lng)
    // Apply only if this is still the latest request — a newer pin-drop/search may have superseded
    // it while the request was in flight; applying now would show a stale score for the old location.
    if (!deps.isCurrent()) return
    deps.applyResult(result)
  } catch (err) {
    // Same guard on the error path: a superseded request must not clobber the current one's state
    // with a stale error (e.g. an old out-of-coverage 404 landing after a fresh in-coverage result).
    if (!deps.isCurrent()) return
    deps.applyError(deps.mapError(err))
  } finally {
    // Only the latest request clears loading; a superseded request finishing must leave `loading`
    // true so the panel keeps showing the spinner for the in-flight current request.
    if (deps.isCurrent()) deps.finish()
  }
}
